import React, { useEffect, useMemo, useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useOutletContext, useParams } from "react-router-dom";
import { db } from "../firebase/firebase";
import "./ColorComponent.scss";
import {
  DEFAULT_THEME,
  createThemeStyle,
  normalizeHex,
  sanitizeTheme,
} from "../utils/themeUtils";

const FIELD_LIST = [
  {
    key: "primary",
    label: "Primary",
    description: "Main buttons, key highlights, important accents",
  },
  {
    key: "secondary",
    label: "Secondary",
    description: "Support color, soft highlights, decorative elements",
  },
  {
    key: "accent",
    label: "Accent",
    description: "Special emphasis, glow, dividers, premium details",
  },
  {
    key: "surface",
    label: "Surface",
    description: "Cards, panels, containers, soft backgrounds",
  },
  {
    key: "text",
    label: "Text",
    description: "Main headings and content text color",
  },
];

const PRESET_THEMES = [
  {
    id: "gold-classic",
    name: "Classic Gold",
    colors: {
      primary: "#C9A14A",
      secondary: "#F4E7C7",
      accent: "#E6C77A",
      surface: "#FFFDF8",
      text: "#2B2B2B",
    },
  },
  {
    id: "blush-romance",
    name: "Blush",
    colors: {
      primary: "#C97B84",
      secondary: "#F6D9DD",
      accent: "#E8AAB3",
      surface: "#FFF8F9",
      text: "#3D2A2E",
    },
  },
  {
    id: "sage-luxury",
    name: "Sage",
    colors: {
      primary: "#7A9D8C",
      secondary: "#DCE8E1",
      accent: "#A8C3B3",
      surface: "#F9FCFA",
      text: "#26312C",
    },
  },
  {
    id: "royal-navy",
    name: "Navy",
    colors: {
      primary: "#223A5E",
      secondary: "#D9E2F0",
      accent: "#8FA9C9",
      surface: "#F8FAFD",
      text: "#1E2430",
    },
  },
  {
    id: "lavender-dream",
    name: "Lavender",
    colors: {
      primary: "#8D7AB8",
      secondary: "#E8E1F6",
      accent: "#B9A7DD",
      surface: "#FCFAFF",
      text: "#322B42",
    },
  },
];

const areThemesEqual = (a, b) => {
  const cleanA = sanitizeTheme(a || DEFAULT_THEME);
  const cleanB = sanitizeTheme(b || DEFAULT_THEME);

  return (
    cleanA.primary === cleanB.primary &&
    cleanA.secondary === cleanB.secondary &&
    cleanA.accent === cleanB.accent &&
    cleanA.surface === cleanB.surface &&
    cleanA.text === cleanB.text
  );
};

const ColorComponent = () => {
  const { slug } = useParams();
  const { weddingData } = useOutletContext();

  const initialTheme = useMemo(
    () => sanitizeTheme(weddingData?.theme || DEFAULT_THEME),
    [weddingData]
  );

  const [theme, setTheme] = useState(initialTheme);
  const [savedTheme, setSavedTheme] = useState(initialTheme);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const clean = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);
    setTheme(clean);
    setSavedTheme(clean);
    setHasUnsavedChanges(false);
  }, [weddingData]);

  const previewStyle = useMemo(() => createThemeStyle(theme), [theme]);

  const saveTheme = async (nextTheme, shouldReload = false) => {
    try {
      setIsSaving(true);
      setFeedback("");

      const cleanTheme = sanitizeTheme(nextTheme);

      await updateDoc(doc(db, "sites", slug), {
        theme: cleanTheme,
        updatedAt: serverTimestamp(),
      });

      setTheme(cleanTheme);
      setSavedTheme(cleanTheme);
      setHasUnsavedChanges(false);
      setFeedback("Theme saved successfully.");

      if (shouldReload) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Theme save failed:", error);
      setFeedback("Failed to save theme. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (key, value) => {
    setTheme((prev) => {
      const next = {
        ...prev,
        [key]: normalizeHex(value, prev[key]),
      };
      setHasUnsavedChanges(!areThemesEqual(next, savedTheme));
      return next;
    });

    setFeedback("");
  };

  const handleTextInputChange = (key, value) => {
    const cleaned = value.replace(/\s/g, "").toUpperCase();

    setTheme((prev) => {
      const next = {
        ...prev,
        [key]: cleaned.startsWith("#") ? cleaned : `#${cleaned}`,
      };
      setHasUnsavedChanges(!areThemesEqual(next, savedTheme));
      return next;
    });

    setFeedback("");
  };

  const handleTextInputBlur = (key) => {
    setTheme((prev) => {
      const next = {
        ...prev,
        [key]: normalizeHex(prev[key], DEFAULT_THEME[key]),
      };
      setHasUnsavedChanges(!areThemesEqual(next, savedTheme));
      return next;
    });
  };

  const handleApplyPreset = async (presetColors) => {
    const cleanPreset = sanitizeTheme(presetColors);
    setTheme(cleanPreset);
    setFeedback("Applying color combination...");
    await saveTheme(cleanPreset, true);
  };

  const handleReset = () => {
    const resetTheme = sanitizeTheme(DEFAULT_THEME);
    setTheme(resetTheme);
    setHasUnsavedChanges(!areThemesEqual(resetTheme, savedTheme));
    setFeedback("Default theme loaded. Click Save Theme to apply it.");
  };

  const handleSaveClick = async () => {
    await saveTheme(theme, true);
  };

  return (
    <div className="siteColorsPage" style={previewStyle}>
      <div className="siteColorsPage__header">
        <div>
          <p className="siteColorsPage__eyebrow">Wedding Website Theme</p>
          <h1 className="siteColorsPage__title">Customize Website Colors</h1>
          <p className="siteColorsPage__subtitle">
            Pick a ready-made palette or change colors manually. Preset
            combinations save automatically, while manual color changes need
            Save Theme.
          </p>
        </div>

        <div className="siteColorsPage__actions">
          <button
            type="button"
            className="siteColorsPage__secondaryBtn"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset
          </button>

          <button
            type="button"
            className="siteColorsPage__primaryBtn"
            onClick={handleSaveClick}
            disabled={isSaving || !hasUnsavedChanges}
          >
            {isSaving ? "Saving..." : "Save Theme"}
          </button>
        </div>
      </div>

      {feedback ? (
        <div
          className={`siteColorsPage__feedback ${
            feedback.toLowerCase().includes("failed")
              ? "siteColorsPage__feedback--error"
              : "siteColorsPage__feedback--success"
          }`}
        >
          {feedback}
        </div>
      ) : null}

      <section className="siteColorsPage__panel siteColorsPage__panel--compact">
        <div className="siteColorsPage__presetHead">
          <div>
            <h2 className="siteColorsPage__panelTitle">Suggested Combinations</h2>
            <p className="siteColorsPage__presetText">
              Tap one to apply instantly.
            </p>
          </div>
        </div>

        <div className="siteColorsPage__presetGrid">
          {PRESET_THEMES.map((preset) => (
            <button
              type="button"
              className="presetThemePill"
              key={preset.id}
              onClick={() => handleApplyPreset(preset.colors)}
              title={preset.name}
              disabled={isSaving}
            >
              <span className="presetThemePill__name">{preset.name}</span>

              <span className="presetThemePill__swatches">
                <span
                  className="presetThemePill__swatch"
                  style={{ background: preset.colors.primary }}
                />
                <span
                  className="presetThemePill__swatch"
                  style={{ background: preset.colors.secondary }}
                />
                <span
                  className="presetThemePill__swatch"
                  style={{ background: preset.colors.accent }}
                />
                <span
                  className="presetThemePill__swatch"
                  style={{ background: preset.colors.surface }}
                />
                <span
                  className="presetThemePill__swatch presetThemePill__swatch--text"
                  style={{ background: preset.colors.text }}
                />
              </span>
            </button>
          ))}
        </div>
      </section>

      <div className="siteColorsPage__grid">
        <section className="siteColorsPage__panel">
          <h2 className="siteColorsPage__panelTitle">Theme Colors</h2>

          <div className="siteColorsPage__fields">
            {FIELD_LIST.map((field) => (
              <div className="siteColorsPage__field" key={field.key}>
                <div className="siteColorsPage__fieldTop">
                  <div>
                    <label className="siteColorsPage__label">{field.label}</label>
                    <p className="siteColorsPage__desc">{field.description}</p>
                  </div>

                  <div
                    className="siteColorsPage__swatch"
                    style={{
                      background: normalizeHex(
                        theme[field.key],
                        DEFAULT_THEME[field.key]
                      ),
                    }}
                    aria-hidden="true"
                  />
                </div>

                <div className="siteColorsPage__inputRow">
                  <input
                    type="color"
                    className="siteColorsPage__picker"
                    value={normalizeHex(
                      theme[field.key],
                      DEFAULT_THEME[field.key]
                    )}
                    onChange={(e) => handleColorChange(field.key, e.target.value)}
                  />

                  <input
                    type="text"
                    className="siteColorsPage__hexInput"
                    value={theme[field.key]}
                    onChange={(e) =>
                      handleTextInputChange(field.key, e.target.value)
                    }
                    onBlur={() => handleTextInputBlur(field.key)}
                    maxLength={7}
                    spellCheck={false}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="siteColorsPage__panel">
          <h2 className="siteColorsPage__panelTitle">Live Preview</h2>

          <div className="themePreview" style={previewStyle}>
            <div className="themePreview__hero">
              <span className="themePreview__badge">Wedding Invitation</span>
              <h3 className="themePreview__names">
                {weddingData?.bride_name || "Bride"} &amp;{" "}
                {weddingData?.groom_name || "Groom"}
              </h3>
              <p className="themePreview__text">
                This preview shows how your saved colors will be used across the
                wedding website.
              </p>
              <button type="button" className="themePreview__button">
                Open Invitation
              </button>
            </div>

            <div className="themePreview__cards">
              <div className="themePreview__card">
                <h4>Primary Usage</h4>
                <p>Main buttons and important elements.</p>
              </div>

              <div className="themePreview__card">
                <h4>Surface Usage</h4>
                <p>Cards, sections, and content blocks.</p>
              </div>

              <div className="themePreview__card themePreview__card--accent">
                <h4>Accent Usage</h4>
                <p>Premium details, highlights, and small decorative touches.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ColorComponent;